import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { Button, Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem } from '@mui/material';
import { Stack, Box } from '@mui/system';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteDocuments } from 'src/shared/services/documentService';

type DocumentOverviewGridFooterButtonProps = {
  selectionModel: GridRowSelectionModel;
  fetchData: () => void;
  setSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
};

export const DocumentOverviewGridFooterButton = (props: DocumentOverviewGridFooterButtonProps) => {
  const { selectionModel, fetchData, setSelectionModel } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [open, setOpen] = useState(false);

  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleDeleteDocuments = async () => {
    try {
      await deleteDocuments(selectionModel as number[]);
      if (isMounted()) {
        setSelectionModel([]);
        toast.success(<>{selectionModel.length > 1 ? t('documentArchive:deletedMultipleSuccess') : t('documentArchive:deletedSuccess')}</>);
        fetchData();
      }
    } catch (error) {
      ConsoleHelper.log(error);
    }
  };

  return (
    <>
      <DeleteConfirmationDialog
        onClose={(val) => {
          if (val) {
            handleDeleteDocuments();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
        title={selectionModel.length > 1 ? t('documentArchive:deleteDocuments') : t('documentArchive:deleteDocument')}
        modaltext={t('common:irreversibleAction')}
        value=''
      />
      <Stack direction='row' spacing={2} sx={{ ml: 2 }}>
        <Box>
          <Button
            ref={anchorRef}
            id='composition-button'
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup='true'
            onClick={() => {
              handleToggle();
            }}
            variant='contained'
            disableElevation
            endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
          >
            {t('common:button.handle')}
          </Button>
          <Popper open={open} anchorEl={anchorRef.current} placement='bottom-start' transition disablePortal nonce={undefined}>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id='composition-menu' aria-labelledby='composition-button'>
                      <MenuItem onClick={() => setOpenDeleteDialog(true)}>
                        {selectionModel.length > 1 ? t('documentArchive:deleteDocuments') : t('documentArchive:deleteDocument')}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Stack>
    </>
  );
};
